/* eslint-disable @next/next/no-img-element */
import React from 'react';
import styles from './style.module.scss';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from 'components/ui/dialog';
import useOutsideClickMultiple from 'hooks/useOutsideClickMultiple';
import Link from 'next/link';
import {
  getNewDate,
  getDayOfTheWeekFromDate,
  getHoursAndMinutesFromISODate,
  convertTimezoneBasedOnDate,
  getTimezonesData
} from 'shared/utils';
import useRouterStuff from 'hooks/useRouterStuff';
import { useEventsContext } from 'hooks/useContexts';

const DEFAULT_IMAGE = '/images/event-broadcom-large.png';
const DEFAULT_PARTNER_IMAGE = '/images/event_banner_def.svg';
const DEFAULT_LOGO = '/images/broadcom-icon.png';
const DEFAULT_WEBCAST = '/images/event-webcast-large.png';
const DEFAULT_CONFERENCE = '/images/event-conference-large.png';
const DEFAULT_USER_GROUP = '/images/event-user-group-large.png';
const DEFAULT_TRAINING = '/images/event-training-large.png';
const DEFAULT_CSP = '/images/csp-event-large.png';

export default function EventModalShow({
  changeSetOpen,
  isOpen,
  doNotShowViewAllPartner = false
}) {
  const { t } = useTranslation();
  const {
    selectedEvent,
    setSelectedEvent,
    allTimezones,
    usersTimezoneLocation
  } = useEventsContext();
  const ref = useRef();
  const timezonesData = getTimezonesData(allTimezones, usersTimezoneLocation);

  const { queryParams, searchParamsObj } = useRouterStuff();

  const selectedEventBanner = selectedEvent?.image
    ? selectedEvent?.image
    : selectedEvent?.locatorCompanyId
    ? DEFAULT_CSP
    : selectedEvent?.partnerId
    ? DEFAULT_PARTNER_IMAGE
    : selectedEvent?.EventTypes.name === 'WEBCAST_ONLINE_PRESENTATION'
    ? DEFAULT_WEBCAST
    : selectedEvent?.EventTypes.name.includes('CONFERENCE')
    ? DEFAULT_CONFERENCE
    : selectedEvent?.EventTypes.name === 'USER_GROUP'
    ? DEFAULT_USER_GROUP
    : selectedEvent?.EventTypes.name === 'PRODUCT_TRAINING'
    ? DEFAULT_TRAINING
    : DEFAULT_IMAGE;

  const [imageLoad, setImageLoad] = useState(false);

  const areDatesEqual = (date1, date2) => {
    return (
      new Date(date1).toISOString().split('T')[0] !==
      new Date(date2).toISOString().split('T')[0]
    );
  };

  const getDateToDisplay = (date) => {
    return `${getDayOfTheWeekFromDate(
      getNewDate({
        date: date,
        returnDateObject: true
      }),
      t
    )}. ${getNewDate({
      date: date,
      returnDateObject: true,
      formatUSDate: true
    })}`;
  };

  const displayDate = () => {
    return selectedEvent.allDayEvent ? (
      <span>
        {getDateToDisplay(selectedEvent.startDate)}{' '}
        {areDatesEqual(selectedEvent.startDate, selectedEvent.endDate) &&
          `to ${getDateToDisplay(selectedEvent.endDate)}`}{' '}
        - All Day
      </span>
    ) : (
      <div className={styles.dates}>
        <div className={styles.date_start}>
          <span>
            {getDateToDisplay(selectedEvent.startDate)} -{' '}
            {getHoursAndMinutesFromISODate(selectedEvent.startDate)}
          </span>
        </div>
        <div className={styles.center_line} />
        <div className={styles.date_end}>
          <span>
            {getDateToDisplay(selectedEvent.endDate)} -{' '}
            {getHoursAndMinutesFromISODate(selectedEvent.endDate)}
          </span>
        </div>
      </div>
    );
  };

  const Image = () => {
    return (
      <img
        src={selectedEventBanner}
        className={styles.banner}
        alt="background-img"
        onLoad={() => setImageLoad(true)}
        hidden={!imageLoad}
      />
    );
  };

  useEffect(() => {
    setImageLoad(false);
  }, [selectedEvent]);

  useOutsideClickMultiple([ref], () => {
    setSelectedEvent(null);
    queryParams({ del: ['eventId'] });
  });

  return !selectedEvent ? (
    <></>
  ) : imageLoad ? (
    <Dialog onOpenChange={changeSetOpen} open={isOpen}>
      <DialogContent
        className="md:p-6 md:pt-20 pt-20 gap-4 h-full"
        onPointerDownOutside={() => {
          setSelectedEvent(null);
          changeSetOpen(false);
        }}
        closeCallback={() => {
          setSelectedEvent(null);
          changeSetOpen(false);
          queryParams({ del: ['eventId'] });
        }}
      >
        <div className={styles.modal_container}>
          <div className={styles.banner_and_logo}>
            <Image />
            <img
              className={styles.logo}
              src={
                selectedEvent?.Partner?.logo ||
                selectedEvent?.LocatorCompany?.logo ||
                DEFAULT_LOGO
              }
              alt="event-logo"
            />
          </div>
          <div className={styles.event_info}>
            <h1>{selectedEvent.title}</h1>
            <div className={styles.line} />
            <span>{selectedEvent.tagline}</span>
            <div className={styles.line} />
            <span>{selectedEvent.EventTypes?.label}</span>
            <div className={styles.line} />
            <span className={styles.desc}>{selectedEvent.description}</span>
            <div className={styles.line} />
            <span>
              {convertTimezoneBasedOnDate(
                selectedEvent.timezone,
                selectedEvent.startDate,
                timezonesData,
                false
              )}
            </span>
            <div className={styles.line} />
            {displayDate()}
            <div className={styles.line} />
            <span>{selectedEvent.region?.name}</span>
            <div className={styles.line} />
            {selectedEvent.location && (
              <>
                <span>{selectedEvent.location}</span>
                <div className={styles.line} />
              </>
            )}
            {selectedEvent.link && (
              <>
                <a
                  href={
                    /^http/i.test(selectedEvent.link)
                      ? selectedEvent.link
                      : `https://${selectedEvent.link}`
                  }
                  rel="noreferrer"
                  target="_blank"
                >
                  {t('link')}
                </a>
                <div className={styles.line} />
              </>
            )}
            {selectedEvent.products?.length > 0 && (
              <span className={styles.products}>{t('products')}:</span>
            )}{' '}
            {selectedEvent.products?.map((prod, index) => {
              return index === selectedEvent.products.length - 1 ? (
                <span>{prod.level3}</span>
              ) : (
                <span>{prod.level3}, </span>
              );
            })}
          </div>
          <div className={styles.line} />
          <div className={styles.view_all_events}>
            {!doNotShowViewAllPartner &&
              selectedEvent.Partner !== null &&
              !searchParamsObj?.slug && (
                <Link
                  className={styles.learn_more}
                  href={`${`/dashboard/partner/${selectedEvent.Partner?.slug}?tab=cal&sub_tab=events`}`}
                  target="_blank"
                >
                  {t('view_all_partner_events')}
                </Link>
              )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  ) : (
    <Image />
  );
}
